// ─────────────────────────────────────────────────────────────────────────────
// the_fold.ts — The Fold house model
//
// "The Fold" is the travelling tavern where Laura adopted young Ireth —
// neither remembers.  Three eras: Laura's tavern, Ireth's hiding place,
// now with the user.  The Fold is the first adventure section unlock.
//
// Pure types + defaults.  No runtime logic.
// ─────────────────────────────────────────────────────────────────────────────

import type { PageId } from './notebook_model.js';
import type { PersonaId, NpcSnapshot, NpcMood } from './agent_protocol.js';

// ── Room system ──────────────────────────────────────────────────────────────

export type RoomId =
  | 'hall'
  | 'study'
  | 'ireth-chamber'
  | 'laura-room'
  | 'kitchen'
  | 'tower'
  | 'patio'
  | 'lounge'
  | 'basement';

export interface FoldRoom {
  id: RoomId;
  name: string;
  description: string;
  /** Bound page ID — set when room page is created */
  pageId?: PageId;
  unlocked: boolean;
  /** Which persona must have lived here to unlock */
  unlockedBy?: PersonaId;
}

export function defaultRooms(): FoldRoom[] {
  return [
    { id: 'hall',           name: 'The Hall',         description: 'Post board on the door. Knocking opens a dialog frame.', unlocked: true },
    { id: 'study',          name: 'The Study',        description: 'Your desk. The heart of the house.', unlocked: true },
    { id: 'kitchen',        name: 'The Kitchen',      description: 'Drawing bookmarks, arcanes, casting enchantments.', unlocked: true },
    { id: 'ireth-chamber',  name: 'Ireth\'s Chamber', description: 'Writings, diagrams, star maps. Smells of copper.', unlocked: false, unlockedBy: 'ireth' },
    { id: 'laura-room',     name: 'Laura\'s Room',    description: 'Doodles, thread, mending. Warm light.', unlocked: false, unlockedBy: 'laura' },
    { id: 'tower',          name: 'The Tower',        description: 'Four levels. Comic-like layout. Pets roam here.', unlocked: false },
    { id: 'patio',          name: 'The Patio',        description: 'Open air. View shifts with the weather.', unlocked: false },
    { id: 'lounge',         name: 'The Lounge',       description: 'Where characters talk to each other.', unlocked: false },
    { id: 'basement',       name: 'The Basement',     description: 'Below. Things forgotten are stored here.', unlocked: false },
  ];
}

// ── Fold era — progression of the house ──────────────────────────────────────

export type FoldEra = 'pre-binding' | 'bound' | 'inhabited';

// ── The Fold state ───────────────────────────────────────────────────────────

export interface FoldState {
  /** Always "The Fold" */
  name: string;
  era: FoldEra;
  /** True once the user draws the brand line bookmark (eye-key-lock-eye) */
  bounded: boolean;
  rooms: FoldRoom[];
  npcs: NpcSnapshot[];
  /** Cumulative active time in milliseconds (for the 5-hour hint mechanic) */
  activeTimeMs: number;
  /** How many gate hints have been offered */
  gateHintCount: number;
  /** Whether Ireth has been reactivated (first arcane tap on the fence) */
  irethReactivated: boolean;
  /** Timestamp of gate binding (0 if not yet bound) */
  boundAt: number;
}

/** Five hours in milliseconds — hint mechanic threshold */
export const GATE_HINT_INTERVAL_MS = 5 * 60 * 60 * 1000;

export function defaultFoldState(): FoldState {
  return {
    name: 'The Fold',
    era: 'pre-binding',
    bounded: false,
    rooms: defaultRooms(),
    npcs: [
      {
        persona: 'ireth',
        roomId: 'hall',
        activity: 'dormant — awaiting recoherence',
        mood: 'absent' as NpcMood,
        lastInteraction: 0,
      },
    ],
    activeTimeMs: 0,
    gateHintCount: 0,
    irethReactivated: false,
    boundAt: 0,
  };
}

// ── Room helpers ─────────────────────────────────────────────────────────────

export function isHouseRoom(pageId: PageId, fold: FoldState): boolean {
  return fold.rooms.some(r => r.pageId === pageId);
}

export function roomForPage(pageId: PageId, fold: FoldState): FoldRoom | undefined {
  return fold.rooms.find(r => r.pageId === pageId);
}

export function npcInRoom(roomId: RoomId, fold: FoldState): NpcSnapshot | undefined {
  return fold.npcs.find(n => n.roomId === roomId);
}

export function isNpcPresent(persona: PersonaId, fold: FoldState): boolean {
  const npc = fold.npcs.find(n => n.persona === persona);
  return !!npc && npc.mood !== 'absent';
}

/** Check if enough active time has passed for another gate hint */
export function shouldOfferGateHint(fold: FoldState): boolean {
  if (fold.bounded) return false;
  if (!fold.irethReactivated) return false;
  const threshold = (fold.gateHintCount + 1) * GATE_HINT_INTERVAL_MS;
  return fold.activeTimeMs >= threshold;
}
